'use client';

import { UAEntityTypes } from '@unifyapps/defs/types/entities';
import useEventCallback from '@unifyapps/hooks/useEventCallback';
import { getIconFromRegistry } from '@unifyapps/icons/utils/getIconFromRegistry';
import { lookup } from '@unifyapps/network/generated/lookup-rest-api/lookup-rest-api';
import type { LookupByKeysRequest } from '@unifyapps/network/generated/models/lookupByKeysRequest';
import { LookupByKeysRequestType } from '@unifyapps/network/generated/models/lookupByKeysRequestType';
import type { Notification as NotificationType } from '@unifyapps/network/generated/models/notification';
import { useGetQueryMeta } from '@unifyapps/network/hooks/useGetQueryMeta';
import { UA_THEME_VARS } from '@unifyapps/style/themes/consts';
import { useSnackbar } from '@unifyapps/ui/components/Snackbar';
import { clsx } from 'clsx';
import type { ReactNode } from 'react';
import { useScreen } from '../../../hooks/useScreen';
import { safeJsonParse } from '../../../utils/json';
import HtmlContent from '../../HtmlContent';
import { useNotificationCategories } from '../hooks/useNotificationCategories';
import { useUserInboxSubscription } from '../hooks/useUserInboxSubscription';
import type { Cta, Notification, NotificationEventTypesLookupResponse } from '../types';

type Props = {
  children: ReactNode;
  topics?: string[];
  loggedInUserId?: string;
  onCtaClick?: (args: { cta?: Cta }) => void;
};

const DESKTOP_ANCHOR_ORIGIN = { vertical: 'top', horizontal: 'right' } as const;
const NON_DESKTOP_ANCHOR_ORIGIN = { vertical: 'top', horizontal: 'center' } as const;
const NOTIFICATIONS_SNACKBAR_AUTO_HIDE_DURATION = 5000;

export function NotificationsSnackbarProvider({
  children,
  loggedInUserId,
  topics,
  onCtaClick,
}: Props) {
  const { isDesktop } = useScreen();

  const { showSnackbar, closeSnackbar } = useSnackbar();

  const queryMeta = useGetQueryMeta();

  const { categories } = useNotificationCategories();

  const onNotificationReceive = useEventCallback(async (payload: Buffer) => {
    const notificationPayload = safeJsonParse(payload.toString());
    const { notificationEventType } = notificationPayload as NotificationType;

    const { response } = (await lookup(
      {
        type: LookupByKeysRequestType.ByKeys,
        lookupType: 'ENTITY',
        keys: [notificationEventType],
        options: {
          entity_type: UAEntityTypes.NotificationEventType,
        },
      } as LookupByKeysRequest,
      { meta: queryMeta },
    )) as NotificationEventTypesLookupResponse;

    const category = notificationEventType
      ? response?.objects?.[notificationEventType]?.properties?.category
      : undefined;

    const notification = {
      ...notificationPayload,
      category: category ? categories?.[category] : undefined,
    } as Notification;

    const { name: iconName } = notification?.category?.icon ?? {
      name: 'InfoCircle',
    };

    showSnackbar({
      anchorOrigin: isDesktop ? DESKTOP_ANCHOR_ORIGIN : NON_DESKTOP_ANCHOR_ORIGIN,
      className: clsx(UA_THEME_VARS, '!mt-0 pointer-events-auto'),
      color: notification.category?.color ?? 'default',
      description: (
        <HtmlContent
          autofocus={false}
          className="self-center"
          color="text-tertiary"
          // typecasting here since it is checked above that content is present
          value={(notification as Notification).content as string}
          variant="text-sm"
          weight="regular"
        />
      ),
      startDecoratorComponent: getIconFromRegistry(iconName) ?? (() => null),
      autoHideDuration: notification?.previewTime
        ? notification.previewTime * 1000
        : NOTIFICATIONS_SNACKBAR_AUTO_HIDE_DURATION,
      actions:
        notification?.cta?.name && notification?.cta?.target !== 'drawer' && onCtaClick
          ? [
              {
                text: notification?.cta?.name,
                onClick: () => {
                  onCtaClick({ cta: notification?.cta });
                },
                variant: 'outline',
                className: 'py-xs',
              },
            ]
          : undefined,
    });
  });

  useUserInboxSubscription({
    loggedInUserId,
    // eslint-disable-next-line @typescript-eslint/no-misused-promises -- not needed
    onNotificationReceive,
    topics,
  });

  return children;
}
